import { useState, useEffect } from 'react';
import { communicationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Shield, Check, Loader2, AlertTriangle, Clock, ChevronRight,
  Building2, FileText, Send, RefreshCw, CheckCircle2, XCircle,
  ArrowLeft, Info,
} from 'lucide-react';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Shield },
  draft: { label: 'Draft', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: FileText },
  submitted: { label: 'Submitted', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Send },
  pending_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
};

const BUSINESS_TYPES = [
  { value: '', label: 'Select business type...' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'nonprofit', label: 'Nonprofit' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

// Onboarding steps
const STEPS = ['info', 'review', 'status'];

export function TrustHubOnboarding({ cnamNumbers }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [step, setStep] = useState('intro'); // intro | info | review | status
  const [form, setForm] = useState({
    business_name: '', business_type: '', ein: '',
    street_address: '', city: '', state: '', zip_code: '',
    contact_email: '', contact_phone: '', website_url: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await communicationsAPI.getTrustHub();
      const data = res.data || res;
      setProfile(data);

      // Populate form from saved data — runs for ANY status so drafts pre-fill even
      // when status is still 'not_started' (e.g. owner started typing then reloaded).
      setForm({
        business_name: data.business_name || '',
        business_type: data.business_type || '',
        ein: data.ein || '',
        street_address: data.street_address || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        website_url: data.website_url || '',
      });

      // Step routing — independent of the not_started guard so drafts auto-skip the intro.
      if (['submitted', 'pending_review', 'approved', 'rejected', 'failed'].includes(data.status)) {
        setStep('status');
      } else if (data.business_name || data.ein) {
        // Tenant has started filling the form previously — skip the intro.
        setStep('info');
      }
    } catch {
      toast.error('Failed to load business verification profile');
    }
    setLoading(false);
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.business_name.trim()) e.business_name = 'Required';
    if (!form.business_type) e.business_type = 'Required';
    if (!form.street_address.trim()) e.street_address = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.state) e.state = 'Required';
    if (!form.zip_code.trim()) e.zip_code = 'Required';
    if (!form.contact_email.trim()) e.contact_email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) e.contact_email = 'Invalid email';
    if (!form.contact_phone.trim()) e.contact_phone = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!validate()) { toast.error('Please fix the highlighted errors.'); return; }
    setSaving(true);
    try {
      const res = await communicationsAPI.saveTrustHub(form);
      setProfile(res.data || res);
      toast.success('Draft saved');
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await communicationsAPI.submitTrustHub();
      const data = res.data || res;
      setProfile(data);
      toast.success('Profile submitted for review');
      setStep('status');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Submission failed';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed');
    }
    setSubmitting(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await communicationsAPI.syncTrustHub();
      const data = res.data || res;
      if (data.synced && data.profile) {
        setProfile(data.profile);
        toast.success(`Status synced: ${data.profile.status}`);
      } else {
        toast.info(data.message || 'No updates available');
      }
    } catch {
      toast.error('Failed to sync status');
    }
    setSyncing(false);
  };

  const handleEditDraft = () => {
    setStep('info');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const status = profile?.status || 'not_started';
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  const StatusIcon = sc.icon;

  // Pending CNAM numbers that need Trust Hub
  const pendingCnamCount = (cnamNumbers || []).filter(n => n.cnam_status === 'pending').length;

  // ── Not Started: Landing ──
  if (status === 'not_started' && step === 'intro') {
    return (
      <div className="space-y-4" data-testid="trust-hub-landing">
        <Card>
          <CardContent className="py-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Business Verification</h3>
                <p className="text-xs text-muted-foreground">Required for outbound Caller ID (CNAM) registration</p>
              </div>
            </div>

            {pendingCnamCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="cnam-pending-notice">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-amber-800">
                    {pendingCnamCount} number{pendingCnamCount !== 1 ? 's' : ''} with CNAM pending
                  </p>
                  <p className="text-[10px] text-amber-700 mt-0.5">
                    Your caller names are saved but won't propagate until business verification is complete.
                  </p>
                </div>
              </div>
            )}

            <div className="bg-muted/50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium">Why is this needed?</p>
              <ul className="text-[11px] text-muted-foreground space-y-1.5">
                <li className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-blue-500" />
                  Business verification is required to register outbound caller names (CNAM) with carriers.
                </li>
                <li className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-blue-500" />
                  Verified businesses get their name displayed on recipient phones instead of a generic number.
                </li>
                <li className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-blue-500" />
                  The process typically takes 24-48 hours for review, plus 48-72 hours for carrier propagation.
                </li>
                <li className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-blue-500" />
                  Not all carriers or devices display caller names — this is a best-effort industry standard.
                </li>
              </ul>
            </div>

            <Button onClick={() => { setStep('info'); setForm(prev => ({ ...prev })); }} className="w-full sm:w-auto" data-testid="start-trust-hub-btn">
              <Building2 className="w-4 h-4 mr-1.5" /> Start Business Verification
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Step 1: Business Info Form ──
  if (step === 'info') {
    return (
      <div className="space-y-4" data-testid="trust-hub-form">
        <div className="flex items-center gap-2">
          <Badge className={`${sc.color} text-[10px]`}><StatusIcon className="w-3 h-3 mr-1" />{sc.label}</Badge>
          <span className="text-xs text-muted-foreground">Step 1 of 3 — Business Information</span>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground uppercase tracking-wide">Business Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FormField label="Business Name *" error={errors.business_name}>
              <Input value={form.business_name} onChange={e => updateField('business_name', e.target.value)} placeholder="Acme Plumbing LLC" data-testid="th-business-name" />
            </FormField>
            <FormField label="Business Type *" error={errors.business_type}>
              <select value={form.business_type} onChange={e => updateField('business_type', e.target.value)} className="w-full text-xs px-3 py-2 rounded-md border bg-background" data-testid="th-business-type">
                {BUSINESS_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
              </select>
            </FormField>
            <FormField label="EIN (Employer ID Number)" error={errors.ein}>
              <Input value={form.ein} onChange={e => updateField('ein', e.target.value)} placeholder="12-3456789" data-testid="th-ein" />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground uppercase tracking-wide">Business Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FormField label="Street Address *" error={errors.street_address}>
              <Input value={form.street_address} onChange={e => updateField('street_address', e.target.value)} placeholder="123 Main St" data-testid="th-street" />
            </FormField>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <FormField label="City *" error={errors.city}>
                <Input value={form.city} onChange={e => updateField('city', e.target.value)} placeholder="Springfield" data-testid="th-city" />
              </FormField>
              <FormField label="State *" error={errors.state}>
                <select value={form.state} onChange={e => updateField('state', e.target.value)} className="w-full text-xs px-3 py-2 rounded-md border bg-background" data-testid="th-state">
                  <option value="">State...</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FormField>
              <FormField label="ZIP Code *" error={errors.zip_code}>
                <Input value={form.zip_code} onChange={e => updateField('zip_code', e.target.value)} placeholder="62701" data-testid="th-zip" />
              </FormField>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground uppercase tracking-wide">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Contact Email *" error={errors.contact_email}>
                <Input type="email" value={form.contact_email} onChange={e => updateField('contact_email', e.target.value)} placeholder="owner@company.com" data-testid="th-email" />
              </FormField>
              <FormField label="Contact Phone *" error={errors.contact_phone}>
                <Input value={form.contact_phone} onChange={e => updateField('contact_phone', e.target.value)} placeholder="+1 (555) 123-4567" data-testid="th-phone" />
              </FormField>
            </div>
            <FormField label="Website (optional)">
              <Input value={form.website_url} onChange={e => updateField('website_url', e.target.value)} placeholder="https://www.company.com" data-testid="th-website" />
            </FormField>
          </CardContent>
        </Card>

        <div className="flex gap-2 pt-1">
          <Button onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Save & Continue
          </Button>
          {profile?.status !== 'not_started' && (
            <Button variant="ghost" onClick={() => setStep('status')} data-testid="back-to-status-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ── Step 2: Review Before Submit ──
  if (step === 'review') {
    return (
      <div className="space-y-4" data-testid="trust-hub-review">
        <div className="flex items-center gap-2">
          <Badge className={`${sc.color} text-[10px]`}><StatusIcon className="w-3 h-3 mr-1" />{sc.label}</Badge>
          <span className="text-xs text-muted-foreground">Step 2 of 3 — Review & Submit</span>
        </div>

        <Card>
          <CardContent className="py-4 space-y-3">
            <p className="text-xs font-medium">Please review your business information before submitting.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <ReviewRow label="Business Name" value={form.business_name} />
              <ReviewRow label="Business Type" value={BUSINESS_TYPES.find(bt => bt.value === form.business_type)?.label || form.business_type} />
              <ReviewRow label="EIN" value={form.ein || 'Not provided'} />
              <ReviewRow label="Address" value={`${form.street_address}, ${form.city}, ${form.state} ${form.zip_code}`} />
              <ReviewRow label="Email" value={form.contact_email} />
              <ReviewRow label="Phone" value={form.contact_phone} />
              {form.website_url && <ReviewRow label="Website" value={form.website_url} />}
            </div>
          </CardContent>
        </Card>

        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="submit-info-note">
          <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-blue-800">
            Submitting will send this information for business verification. Review typically takes 24-48 hours.
            After approval, your CNAM caller names will begin propagating (additional 48-72 hours).
          </p>
        </div>

        <div className="flex gap-2 pt-1">
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-trust-hub-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Submit for Verification
          </Button>
          <Button variant="ghost" onClick={() => setStep('info')} data-testid="edit-draft-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Edit
          </Button>
        </div>
      </div>
    );
  }

  // ── Step 3: Status View ──
  return (
    <div className="space-y-4" data-testid="trust-hub-status">
      <Card>
        <CardContent className="py-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sc.color.split(' ')[0]}`}>
                <StatusIcon className={`w-5 h-5 ${sc.color.split(' ')[1]}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Business Verification</h3>
                  <Badge className={`${sc.color} text-[10px]`}>{sc.label}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{profile?.business_name || 'Business profile'}</p>
              </div>
            </div>
            {profile?.twilio_customer_profile_sid && (
              <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleSync} disabled={syncing} data-testid="sync-status-btn">
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync
              </Button>
            )}
          </div>

          {/* Auto-sync indicator */}
          {profile?.last_webhook_at && (
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground" data-testid="auto-sync-indicator">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Auto-sync active — last update: {new Date(profile.last_webhook_at).toLocaleString()}
            </div>
          )}
          {profile?.twilio_customer_profile_sid && !profile?.last_webhook_at && (
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground" data-testid="auto-sync-pending">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Awaiting first status update from our messaging provider
            </div>
          )}

          {/* Status message */}
          {profile?.error_message && (
            <div className={`rounded-lg px-3 py-2.5 text-xs ${
              status === 'approved' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
              status === 'rejected' || status === 'failed' ? 'bg-red-50 border border-red-200 text-red-800' :
              'bg-amber-50 border border-amber-200 text-amber-800'
            }`} data-testid="trust-hub-status-msg">
              {profile.error_message}
            </div>
          )}

          {/* Next steps guidance */}
          {status === 'submitted' && (
            <NextStepCard
              title="What happens next?"
              items={[
                'Your business information has been saved and queued for review.',
                'Review typically takes 24-48 hours.',
                'Status updates are received automatically — no further action needed from you.',
                'You can click "Sync" to manually check for updates.',
              ]}
            />
          )}
          {status === 'pending_review' && (
            <NextStepCard
              title="Under Review"
              items={[
                'Your business profile is being reviewed. This typically takes 24-48 hours.',
                'Status will update automatically when the review completes.',
                'You can click "Sync" to manually check for updates.',
              ]}
            />
          )}
          {status === 'approved' && (
            <NextStepCard
              title="Approved — What to expect"
              items={[
                'Your business is verified. Caller-ID registration is active.',
                'Caller names will propagate across US carrier networks over the next 48-72 hours.',
                'Not all carriers or devices display caller names — this is an industry-wide limitation.',
                'Numbers with caller-ID set will show your business name on supported devices.',
              ]}
            />
          )}
          {status === 'rejected' && (
            <NextStepCard
              title="Rejected — What to do"
              items={[
                'Your business profile was rejected. This usually means incorrect or incomplete information.',
                'Review your business details and correct any issues.',
                'Common reasons: mismatched EIN, unverifiable address, or missing information.',
                'After corrections, you can resubmit for review.',
              ]}
            />
          )}

          {/* Business info summary */}
          <div className="border-t pt-3 space-y-2">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Submitted Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <ReviewRow label="Business" value={profile?.business_name} />
              <ReviewRow label="Type" value={BUSINESS_TYPES.find(bt => bt.value === profile?.business_type)?.label || profile?.business_type} />
              <ReviewRow label="EIN" value={profile?.ein || 'Not provided'} />
              <ReviewRow label="Address" value={profile?.street_address ? `${profile.street_address}, ${profile.city}, ${profile.state} ${profile.zip_code}` : '—'} />
              <ReviewRow label="Email" value={profile?.contact_email} />
              <ReviewRow label="Phone" value={profile?.contact_phone} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            {(status === 'draft' || status === 'rejected' || status === 'failed') && (
              <Button size="sm" onClick={handleEditDraft} data-testid="edit-profile-btn">
                <FileText className="w-3.5 h-3.5 mr-1" /> Edit & Resubmit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CNAM connection */}
      {pendingCnamCount > 0 && status !== 'approved' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="cnam-trust-hub-link">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-800">
              {pendingCnamCount} number{pendingCnamCount !== 1 ? 's' : ''} waiting on business verification
            </p>
            <p className="text-[10px] text-amber-700 mt-0.5">
              {status === 'pending_review' || status === 'submitted'
                ? 'Your CNAM names are saved but won\'t propagate until business verification is complete.'
                : 'Complete business verification to enable caller name propagation.'}
            </p>
          </div>
        </div>
      )}
      {status === 'approved' && pendingCnamCount > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="cnam-propagating-notice">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-emerald-800">Business verified</p>
            <p className="text-[10px] text-emerald-700 mt-0.5">
              Caller names for your {pendingCnamCount} number{pendingCnamCount !== 1 ? 's' : ''} are now propagating. This may take 48-72 hours.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Helper components ──

function FormField({ label, error, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-600">{error}</p>}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] text-muted-foreground shrink-0 w-20">{label}:</span>
      <span className="text-xs font-medium truncate">{value || '—'}</span>
    </div>
  );
}

function NextStepCard({ title, items }) {
  return (
    <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
      <p className="text-[11px] font-medium">{title}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1.5">
            <ChevronRight className="w-2.5 h-2.5 mt-0.5 shrink-0 text-muted-foreground/50" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
